import React, { useState, useMemo, useEffect, useRef } from "react";
import * as ChartJS from "chart.js";
import {
  ExclamationTriangle,
  Copy,
  ChevronUp,
  ChevronDown,
  ChevronExpand,
  ArrowClockwise,
} from "react-bootstrap-icons";
import { toast } from "react-toastify";

import styles from "./Subjectengagementinsights.module.css";
import { useSelector } from "react-redux";

ChartJS.Chart.register(
  ChartJS.ArcElement,
  ChartJS.Tooltip,
  ChartJS.Legend,
  ChartJS.DoughnutController
);

/* ============================================================================
 * Shared constants
 * ==========================================================================*/
const RISK_BUCKETS = {
  noRisk: {
    label: "No Risk (8 - 10)",
    color: "#22C55E",
    gradient: ["#10b981", "#6ee7b7"],
  },
  mediumRisk: {
    label: "Medium Risk (5 - 7.9)",
    color: "#F97316",
    gradient: ["#f97316", "#fdba74"],
  },
  highRisk: {
    label: "High Risk (0 - 4.9)",
    color: "#DC2626",
    gradient: ["#ef4444", "#fca5a5"],
  },
};

const RISK_KEYS_BY_INDEX = ["noRisk", "mediumRisk", "highRisk"];

const RISK_LABELS = {
  noRisk: "No Risk",
  mediumRisk: "Medium Risk",
  highRisk: "High Risk",
};

const STATUS_TO_BADGE_CLASS = {
  "No Risk": "badgeNoRisk",
  "Medium Risk": "badgeMediumRisk",
  "High Risk": "badgeHighRisk",
};

const BADGE_BY_RISK = {
  noRisk: "badgeNoRisk",
  mediumRisk: "badgeMediumRisk",
  highRisk: "badgeHighRisk",
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* score → risk bucket (8-10 no risk, 5-7 medium, 0-4 high) */
const getScoreRisk = (v) => {
  const n = Number(v) || 0;
  if (n >= 8) return "noRisk";
  if (n >= 5) return "mediumRisk";
  return "highRisk";
};

/* ============================================================================
 * REUSABLE export: ScoreBadge — numeric colored badge (Academic Health /
 * Overall Score). Parent uses it inside a column `render`.
 * ==========================================================================*/
export const ScoreBadge = ({ value }) => {
  const badgeClass = BADGE_BY_RISK[getScoreRisk(value)];
  return (
    <span className={`${styles.statusBadge} ${styles[badgeClass]}`}>
      {value}
    </span>
  );
};

/* ============================================================================
 * Reusable Chart.js plugins
 * ==========================================================================*/
const centerTextPlugin = {
  id: "centerText",
  afterDraw(chart, _args, opts) {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.font = `bold ${opts.fontSize ?? 22}px ${opts.fontFamily ?? "system-ui, sans-serif"}`;
    ctx.fillStyle = opts.color ?? "#1E293B";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(opts.text ?? ""), cx, cy);
    ctx.restore();
  },
};

const segmentLabelPlugin = {
  id: "segmentLabel",
  afterDatasetsDraw(chart, _args, opts) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta) return;
    meta.data.forEach((arc, i) => {
      const value = chart.data.datasets[0].data[i];
      if (!value) return;
      const { x, y } = arc.tooltipPosition();
      ctx.save();
      ctx.font = `bold ${opts.fontSize ?? 13}px ${opts.fontFamily ?? "system-ui, sans-serif"}`;
      ctx.fillStyle = opts.color ?? "#fff";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(value), x, y);
      ctx.restore();
    });
  },
};

/* ============================================================================
 * REUSABLE: SubjectDonutCard
 * ==========================================================================*/
const SubjectDonutCard = ({ summary, label, isSelected, onSegmentClick, selectedRiskKey }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const handlerRef = useRef(onSegmentClick);

  useEffect(() => { handlerRef.current = onSegmentClick; }, [onSegmentClick]);

  const { noRisk = 0, mediumRisk = 0, highRisk = 0 } = summary || {};
  const total = noRisk + mediumRisk + highRisk;
  const selectedIndex = RISK_KEYS_BY_INDEX.indexOf(selectedRiskKey); // -1 if none

  const makeGradient = (chart, [from, to]) => {
    const { ctx, chartArea } = chart;
    if (!chartArea) return from;
    const g = ctx.createLinearGradient(
      chartArea.left, chartArea.top,
      chartArea.right, chartArea.bottom
    );
    g.addColorStop(0, from);
    g.addColorStop(1, to);
    return g;
  };

  const growSelectedSlicePlugin = {
    id: "growSelectedSlice",
    afterUpdate(chart) {
      const idx = chart.$selectedIndex;
      if (idx == null || idx < 0) return;
      const meta = chart.getDatasetMeta(0);
      const arc = meta?.data?.[idx];
      if (!arc) return;
      // grow relative to the chart's computed outer radius (stable)
      const base = meta.controller.outerRadius ?? arc.outerRadius;
      arc.outerRadius = base + 10;
    },
  };

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new ChartJS.Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: ["No Risk", "Medium Risk", "High Risk"],
        datasets: [{
          data: [noRisk, mediumRisk, highRisk],
          backgroundColor: (ctx) => {
            const key = RISK_KEYS_BY_INDEX[ctx.dataIndex];
            return makeGradient(ctx.chart, RISK_BUCKETS[key].gradient);
          },
          borderWidth: 0,
          hoverOffset: 6,
          // ❌ no `offset` here — the grow plugin handles the highlight
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        layout: { padding: 18 },
        animation: false,
        onClick: (_event, elements) => {
          if (elements && elements.length > 0) {
            const idx = elements[0].index;
            const riskKey = RISK_KEYS_BY_INDEX[idx];
            if (riskKey) handlerRef.current?.(riskKey);
          }
        },
        onHover: (event, elements) => {
          const target = event?.native?.target;
          if (target) {
            target.style.cursor = elements.length > 0 ? "pointer" : "default";
          }
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const pct = total ? Math.round((ctx.parsed / total) * 100) : 0;
                return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
              },
            },
          },
          centerText: { text: String(total), fontSize: 22 },
          segmentLabel: { fontSize: 13, color: "#fff" },
        },
      },
      plugins: [centerTextPlugin, segmentLabelPlugin, growSelectedSlicePlugin],
    });

    // ✅ set the selected index, then force one update so the plugin runs
    chartRef.current.$selectedIndex = selectedIndex;
    chartRef.current.update();

    // ✅ cleanup: ONLY destroy
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [noRisk, mediumRisk, highRisk, total, selectedIndex]);

  return (
    <div className={`${styles.subjectCard} ${isSelected ? styles.subjectCardSelected : ""}`}>
      <div className={styles.subjectChartWrap}>
        <canvas ref={canvasRef} />
      </div>
      <div className={styles.subjectLabel}>{label}</div>
    </div>
  );
};

/* ============================================================================
 * REUSABLE: RiskLegend
 * ==========================================================================*/
const SWATCH_BY_RISK = {
  noRisk: "swatchNoRisk",
  mediumRisk: "swatchMediumRisk",
  highRisk: "swatchHighRisk",
};
const RiskLegend = () => (
  <div className={styles.legend}>
    {Object.entries(RISK_BUCKETS).map(([key, b]) => (
      <div key={key} className={styles.legendItem}>
        <span className={`${styles.legendSwatch} ${styles[SWATCH_BY_RISK[key]]}`} />
        <span>{b.label}</span>
      </div>
    ))}
  </div>
);

/* ============================================================================
 * Default columns (staff "row"/engagement variant — unchanged behavior)
 * ==========================================================================*/
const DEFAULT_COLUMNS = [
  { key: "name", label: "Student Name", isName: true },
  { key: "engagementScore", label: "Engagement Score (%)" },
  {
    key: "status",
    label: "Status",
    sortable: false,
    render: (s) => {
      const badgeClass = STATUS_TO_BADGE_CLASS[s.status] || "badgeHighRisk";
      return (
        <span className={`${styles.statusBadge} ${styles[badgeClass]}`}>
          {s.status || "High Risk"}
        </span>
      );
    },
  },
];

const SortIcon = ({ direction }) => {
  if (direction === "asc") return <ChevronUp size={12} className={styles.sortIcon} />;
  if (direction === "desc") return <ChevronDown size={12} className={styles.sortIcon} />;
  return <ChevronExpand size={12} className={`${styles.sortIcon} ${styles.sortIconIdle}`} />;
};

/* ============================================================================
 * REUSABLE: StudentTable — now column-config driven
 * ==========================================================================*/
const StudentTable = ({ columns = DEFAULT_COLUMNS, students, onCopyPhone, handleClick }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sortedStudents = useMemo(() => {
    if (!sortKey) return students;
    const col = columns.find((c) => c.key === sortKey);
    return [...students].sort((a, b) => {
      let av = col?.sortValue ? col.sortValue(a) : a[sortKey];
      let bv = col?.sortValue ? col.sortValue(b) : b[sortKey];
      if (typeof av === "string") av = av.toLowerCase();
      if (typeof bv === "string") bv = bv.toLowerCase();
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [students, columns, sortKey, sortDir]);

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key} className={`${styles.th} ${col.isName ? styles.thName : ""}`}>
              {col.sortable === false ? (
                <span>{col.label}</span>
              ) : (
                <button type="button" onClick={() => handleSort(col.key)} className={styles.sortBtn}>
                  <span>{col.label}</span>
                  <SortIcon direction={sortKey === col.key ? sortDir : null} />
                </button>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedStudents.map((s, i) => (
          <tr key={s.id} className={i % 2 === 1 ? styles.rowAlt : styles.row}>
            {columns.map((col) =>
              col.isName ? (
                <td key={col.key} className={styles.td}>
                  <div className={styles.studentName}>
                    <p
                      className={handleClick ? styles.absenteeName : undefined}
                      onClick={handleClick ? () => handleClick(s.id) : undefined}
                      title={s.name}
                      style={handleClick ? { cursor: "pointer" } : undefined}
                    >
                      {s.name}
                    </p>
                  </div>

                </td>
              ) : (
                <td key={col.key} className={styles.td}>
                  {col.render ? col.render(s) : (s[col.key] ?? "—")}
                </td>
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* ============================================================================
 * Toolbar (page-size + search) and Pagination
 * ==========================================================================*/
const Toolbar = ({
  showPageSize, perPage, onLimitChange, pageSizeOptions,
  showSearch, search, onSearchChange, searchPlaceholder,
}) => (
  <div className={styles.toolbar}>
    {showPageSize ? (
      <select
        value={perPage}
        onChange={(e) => onLimitChange?.(Number(e.target.value))}
        className={styles.limitSelect}
        aria-label="Rows per page"
      >
        {pageSizeOptions.map((n) => (
          <option key={n} value={n}>{n}</option>
        ))}
      </select>
    ) : (
      <span />
    )}

    {showSearch && (
      <input
        type="text"
        value={search}
        onChange={(e) => onSearchChange?.(e.target.value)}
        placeholder={searchPlaceholder || "Search ..."}
        className={styles.searchInput}
        aria-label="Search"
      />
    )}
  </div>
);

const TablePagination = ({ page, perPage, total, hasPrev, hasNext, onPageChange }) => {
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  return (
    <div className={styles.paginationRow}>
      <div className={styles.entriesText}>
        Showing {start} to {end} of {total} entries
      </div>
      <div className={styles.paginationBtns}>
        <button type="button" className={styles.pageBtn} disabled={!hasPrev} onClick={() => onPageChange?.(page - 1)}>
          Previous
        </button>
        <button type="button" className={styles.pageBtn} disabled={!hasNext} onClick={() => onPageChange?.(page + 1)}>
          Next
        </button>
      </div>
    </div>
  );
};

/* ============================================================================
 * State sub-components
 * ==========================================================================*/
const SkeletonState = () => (
  <div className={styles.skeletonBody}>
    <div className={styles.skeletonSubjects}>
      {[...Array(5)].map((_, i) => (
        <div key={i} className={styles.skeletonSubject}>
          <div className={styles.skeletonCircle} />
          <div className={styles.skeletonBar} />
        </div>
      ))}
    </div>
    <div>
      {[...Array(4)].map((_, i) => (
        <div key={i} className={styles.skeletonRow} />
      ))}
    </div>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className={styles.errorState}>
    <ExclamationTriangle size={40} className={styles.errorIcon} />
    <p className={styles.errorText}>Something went wrong.</p>
    {onRetry && (
      <button type="button" onClick={onRetry} className={styles.retryBtn}>
        <ArrowClockwise size={16} /> Retry
      </button>
    )}
  </div>
);

const EmptyTableState = ({ riskLabel, subjectName }) => (
  <div className={styles.emptyTable}>
    No students  found for this {subjectName}.
  </div>
);

/* ============================================================================
 * PARENT: SubjectEngagementInsights
 *
 * Existing props: data, status, layout, title, note, selectedSubjectId,
 *   selectedRiskKey, onSubjectSegmentClick, onRetry
 *
 * NEW (all optional, defaults keep the completed staff screen unchanged):
 *   icon            → header icon node (default ExclamationTriangle)
 *   columns         → table column config (default DEFAULT_COLUMNS)
 *   notePosition    → "header" (default) | "bottom"
 *   showFilter      → boolean — class filter tabs (HM Academic Health)
 *   filterOptions   → [{ value, label }]
 *   filterValue, onFilterChange
 *   showPageSize    → boolean — rows-per-page select
 *   perPage, onLimitChange, pageSizeOptions
 *   showSearch      → boolean — search input
 *   search, onSearchChange, searchPlaceholder
 *   showPagination  → boolean — Previous/Next + "Showing X to Y of Z"
 *   page, total, hasPrev, hasNext, onPageChange
 * ==========================================================================*/
const SubjectEngagementInsights = ({
  data,
  status = "loading",
  layout = "row",
  title,
  note,
  handleClick,
  selectedSubjectId,
  selectedRiskKey = "highRisk",
  onSubjectSegmentClick,
  onRetry,
  // new optional props
  icon,
  columns = DEFAULT_COLUMNS,
  notePosition = "header",
  showFilter = false,
  filterOptions = [],
  filterValue,
  onFilterChange,
  showPageSize = false,
  perPage = 10,
  onLimitChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  showSearch = false,
  search = "",
  onSearchChange,
  searchPlaceholder = "Search ...",
  showPagination = false,
  page = 1,
  total = 0,
  hasPrev = false,
  hasNext = false,
  onPageChange,
}) => {
  const handleCopyPhone = async (phone) => {
    try {
      await navigator.clipboard.writeText(phone);
      toast.success("Phone number copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const selectedSubject = useMemo(() => {
    if (!data?.subjects?.length) return null;
    return data.subjects.find((s) => s.id === selectedSubjectId) || data.subjects[0];
  }, [data, selectedSubjectId]);

  const riskLabel = RISK_LABELS[selectedRiskKey] || "High Risk";
  const isSplit = layout === "split";
  const { translations } = useSelector((state) => state.language);
  const t = useMemo(() => translations?.labels || {}, [translations?.labels]);
  const resolvedTitle =
    title ||
    (isSplit
      ? (t.subjectEngagementRishAnalysis || "Subject Engagement Risk Analysis")
      : (t.subjectWiseEngagementScore || "Subject wise Engagement Score"));

  const showToolbar = showPageSize || showSearch;

  const toolbar = showToolbar ? (
    <Toolbar
      showPageSize={showPageSize}
      perPage={perPage}
      onLimitChange={onLimitChange}
      pageSizeOptions={pageSizeOptions}
      showSearch={showSearch}
      search={search}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
    />
  ) : null;

  const pagination = showPagination ? (
    <TablePagination
      page={page}
      perPage={perPage}
      total={total}
      hasPrev={hasPrev}
      hasNext={hasNext}
      onPageChange={onPageChange}
    />
  ) : null;

  return (
    <div className={styles.card}>
      {/* Header — icon, title, optional filter tabs, optional header-note */}
      <div className={styles.header}>
        <span className={styles.headerIcon} aria-hidden="true">
          {icon || <ExclamationTriangle />}
        </span>
        <h2 className={styles.title}>{resolvedTitle}</h2>
        {note && notePosition === "header" && (
          <div className={styles.noteBanner}>
            <strong>Note:</strong> {note}
          </div>
        )}
        {showFilter && (
          <div className={styles.tabs}>
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`${styles.tab} ${filterValue === opt.value ? styles.tabActive : ""}`}
                onClick={() => onFilterChange?.(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}


      </div>

      {(status === "idle" || status === "loading") && <SkeletonState />}
      {status === "error" && <ErrorState onRetry={onRetry} />}
      {status === "success" && data && (!data.subjects || data.subjects.length === 0) && (
        <div className={styles.emptyTable}>No subjects available.</div>
      )}

      {status === "success" && data && data.subjects?.length > 0 && (
        <>
          {/* ──────────────── SPLIT layout ──────────────── */}
          {isSplit && selectedSubject && (
            <div className={styles.splitBody}>
              {/* Left: donut + legend */}
              <div className={styles.splitChartCol}>
                <SubjectDonutCard
                  summary={selectedSubject.summary}
                  label={selectedSubject.name}
                  isSelected={false}
                  selectedRiskKey={selectedRiskKey}
                  onSegmentClick={(riskKey) =>
                    onSubjectSegmentClick?.(selectedSubject.id, riskKey)
                  }
                />
                <RiskLegend />
              </div>

              {/* Right: toolbar + table + pagination */}
              <div className={styles.splitTableCol}>
                {toolbar}
                {selectedSubject.students?.length > 0 ? (
                  <div className={styles.tableWrap}>
                    <StudentTable
                      columns={columns}
                      students={selectedSubject.students}
                      onCopyPhone={handleCopyPhone}
                      handleClick={handleClick}
                    />
                  </div>
                ) : (
                  <EmptyTableState riskLabel={riskLabel} subjectName={selectedSubject.name} />
                )}
                {pagination}
              </div>
            </div>
          )}

          {/* ──────────────── ROW layout ──────────────── */}
          {!isSplit && (
            <>
              <div className={styles.subjectsRow}>
                {data.subjects.map((subject) => (
                  <SubjectDonutCard
                    key={subject.id}
                    summary={subject.summary}
                    label={subject.name}
                    isSelected={selectedSubject?.id === subject.id}
                    selectedRiskKey={selectedSubject?.id === subject.id ? selectedRiskKey : null}  // ← add
                    onSegmentClick={(riskKey) =>
                      onSubjectSegmentClick?.(subject.id, riskKey)
                    }
                  />
                ))}
              </div>

              <RiskLegend />

              <div className={styles.tableSection}>
                <h3 className={styles.tableTitle}>
                  Students at {riskLabel} in {selectedSubject?.name}
                </h3>

                {toolbar}

                {selectedSubject?.students?.length > 0 ? (
                  <div className={styles.tableWrap}>
                    <StudentTable
                      columns={columns}
                      students={selectedSubject.students}
                      onCopyPhone={handleCopyPhone}
                      handleClick={handleClick}
                    />
                  </div>
                ) : (
                  <EmptyTableState riskLabel={riskLabel} subjectName={selectedSubject?.name} />
                )}

                {pagination}
              </div>
            </>
          )}

          {/* Bottom note banner (HM Academic Health) */}
          {note && notePosition === "bottom" && (
            <div className={styles.noteBottom}>
              <strong>Note:</strong> {note}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SubjectEngagementInsights;
